import { RiNotification3Line, RiNotificationOffLine, RiVolumeMuteLine } from "@remixicon/react";
import { Badge } from "@screener/uikit/components/badge";
import { Button } from "@screener/uikit/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import type { AlertNotificationPermission } from "./alert-notify";

/**
 * Пометки шапки §9 (1.20), design.md решение 3/4: разрешение на нотификации
 * запрашивается кнопкой, а не при загрузке, и заблокированный звук снимается
 * тем же жестом, которым его снимает `alert-sound.ts`. Часть экрана, не
 * компонент UIKit — знает про правила алертов и разрешение браузера.
 */
export interface AlertStatusProps {
  /** Звук заблокирован браузером до первого жеста пользователя. */
  soundBlocked: boolean;
  /** Текущее разрешение браузера на нотификации. */
  permission: AlertNotificationPermission;
  /** У пользователя есть хотя бы одно правило со включённым `sound`. */
  hasSoundRules: boolean;
  /** У пользователя есть хотя бы одно правило со включённым `notify`. */
  hasNotifyRules: boolean;
  onRequestPermission: () => void;
  onUnlockSound: () => void;
}

/** Рост и скругление элементов шапки Алертов: 26 px, radius 6 (макет, §116 п. 3). */
const STATUS_CONTROL = "h-[26px] rounded-md px-2.5 text-xs font-medium";

/**
 * Ниже `md` пометка — только значок 26×26: подпись остаётся в разметке как
 * `sr-only`, а зрячему её показывает тултип. На широком подпись видна, и
 * тултип её не дублирует.
 */
const NARROW_ICON_ONLY = "max-md:w-[26px] max-md:gap-0 max-md:px-0";

const SOUND_BLOCKED_TEXT = "Звук заблокирован — нажмите";
const REQUEST_TEXT = "Включить уведомления";
const DENIED_TEXT = "Уведомления запрещены браузером";

/**
 * Пометки видны, только пока актуальны — без правил `sound`/`notify` в
 * шапке нет ничего (спека `web-alert-sound`, «Правил с нотификацией нет»).
 */
export function AlertStatus({
  soundBlocked,
  permission,
  hasSoundRules,
  hasNotifyRules,
  onRequestPermission,
  onUnlockSound,
}: AlertStatusProps) {
  const showRequestButton = hasNotifyRules && permission === "default";
  const showDeniedBadge = hasNotifyRules && permission === "denied";
  // (DEN-105) Пометка звука нужна тому, кто не дал сайту уведомления:
  // с разрешением звук снимается первым жестом без напоминания в шапке.
  const showSoundBlocked = hasSoundRules && soundBlocked && permission !== "granted";

  // Обёртка всегда в разметке, даже пустая: `aria-live` объявляет только то,
  // что появляется ВНУТРИ уже существующего живого региона — если сам
  // регион смонтировать одновременно с содержимым, скринридер ничего не
  // скажет (ревью stage6-alert-sound, пункт 10).
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-2">
      {showSoundBlocked ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="outline"
                size="sm"
                onClick={onUnlockSound}
                className={cn(STATUS_CONTROL, NARROW_ICON_ONLY)}
              />
            }
          >
            <RiVolumeMuteLine aria-hidden="true" className="size-3.5" />
            <span className="max-md:sr-only">{SOUND_BLOCKED_TEXT}</span>
          </TooltipTrigger>
          <TooltipContent className="md:hidden">{SOUND_BLOCKED_TEXT}</TooltipContent>
        </Tooltip>
      ) : null}
      {showRequestButton ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="outline"
                size="sm"
                onClick={onRequestPermission}
                className={cn(STATUS_CONTROL, NARROW_ICON_ONLY)}
              />
            }
          >
            <RiNotification3Line aria-hidden="true" className="size-3.5" />
            <span className="max-md:sr-only">{REQUEST_TEXT}</span>
          </TooltipTrigger>
          <TooltipContent className="md:hidden">{REQUEST_TEXT}</TooltipContent>
        </Tooltip>
      ) : null}
      {showDeniedBadge ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Badge
                // Значок без подписи ниже md должен объясняться и с клавиатуры:
                // фокус открывает тултип.
                tabIndex={0}
                variant="outline-muted"
                className={cn(STATUS_CONTROL, NARROW_ICON_ONLY, "gap-1.5")}
              />
            }
          >
            <RiNotificationOffLine aria-hidden="true" className="size-3.5" />
            <span className="max-md:sr-only">{DENIED_TEXT}</span>
          </TooltipTrigger>
          <TooltipContent className="md:hidden">{DENIED_TEXT}</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}
